import { v4 as uuidv4, NIL as uuidNull, NIL } from 'uuid';
import { FC, useEffect, useRef, useState } from "react";
import { DocumentKey, IDocumentCreatorFormProps } from "../../../../@types/documents";
import BaseDocumentCreator from "../../../../components/documents/baseDocumentCreator";
import { IInvoiceOutGetDTO, IInvoiceOutUpdateDTO } from "../../../../libs/coreapi-dto/documents/invoiceOut";
import { IInvoiceOutItemEditDTO, IInvoiceOutItemViewDTO } from "../../../../libs/coreapi-dto/documents/invoiceOutItem";
import { LotDataProvider } from "../../../../Services/DataProviders/LotDataProvider";
import useGridFilter from "../../../../system/hooks/useGridFilter";
import { useAppContext } from "../../../../system/providers/appContextProvider";
import useInvoiceOutState from "./invoiceOutState";
import useInvoiceOutValidator from "./invoiceOutValidator";
import styles from "../styles/index.module.scss";
import GridWrapper from "../../../../components/controls/GridWrapper";
import { DateInput, TextAreaInput, TextInput } from "../../../../components/controls/inputs";
import { StoreSelectorPanel } from "../../../../components/storeSelectorPanel";
import ContractorSelectorInput from "../../../../components/controls/inputs/ContractorSelectorInput";
import { SelectControl } from "../../../../components/controls/SelectControl";
import { LotSelectorPanel } from "../../../../components/lotSelector";
import { IInvoiceOutItemActionDataTab, InvoiceOutItemCreatorTab } from "..";
import InvoiceOutItemGridPluginSettings from "../pluginSettings/invoiceOutItemGridPluginSettings";
import PositionEditPanel from "../../../../components/commandsPanels/PositionEditPanel";
import { Accordion } from "../../../../components/controls/accordion";
import KizScanModal from "../../../Dictionaries/Kiz/ScanModal/KizScanModal";
import { DocumentType, DocumentTypeRu, IdTableVariant, SearchOption, SysOptionName } from "../../../../@types/enumsGlobal";
import { ILotDTO } from "../../../../libs/coreapi-dto/accounting/lot";
import { ISearchQueryInfo } from "../../../../components/lotSelector/lotSelectorInput";
import { CheckBox } from "../../../../components/controls/checkbox";
import { ChangeableGrid, IChangeDataGrid } from "../../../../components/grids/changeableGrid/ChangeableGrid";
import { ICostInfoDTO, IEntitySimpleDTO } from "../../../../libs/coreapi-dto/@types/common";
import { MoneyFormatComponent } from "../../../../components/controls/inputs/BaseInput";
import { onPharmacyChanged } from "../../../../system/functions/onPharmacyChanged";
import { onStoreChanged } from "../../../../system/functions/onStoreChanged";
import { checkStorePharmacy } from "../../../../system/functions/checkStorePharmacy";
import { onCanChangeData } from "../../../../system/functions/onCanChangeData";
import useOperativeReserve from "../../../../components/lotSelector/useOperativeReserve";
import { MessageModalWindow } from "../../../../components/modalWindows/MessageModalWindow";
import { useTranslation } from "react-i18next";
import { useTimeout } from "../../../../system/hooks/useTimeout";
import { DateTime } from "luxon";
import useLockingDocuments from "../../../../components/lockDocuments/useLockingDocuments";
import { SelectDocumentModalWindow } from "../../../../components/modalWindows/SelectDocumentModalWindow";
import { InvoiceDataProvider } from "../../../../Services/DataProviders/InvoiceDataProvider";
import { DictionaryInput } from "../../../../components/controls/dictionaryInput";
import { useUserContext } from "../../../../system/providers/userContextProvider";
import { checkLockStatus } from "../../../CommonHelperFunctions";
import { ScalingRatioDataProvider } from "../../../../Services/DataProviders/ScalingRatioDataProvider";
import { useTabsContext } from '../../../../system/providers/tabsProvider';
import FlexRow from '../../../../components/controls/FlexRow';
import { kizCounter } from '../../../../system/functions/sumKiszCount';
import { BarcodeType, IKizBoxDTO, IKizDTO } from '../../../../libs/coreapi-dto/dirs/kiz';
import { validateDuplicateKizs } from '../../../../system/functions/validateDuplicateKizs';
import renderGlobalAlert from '../../../../system/hooks/useGlobalAlert';
import { roundMoneyFormat } from '../../../../system/functions/round';
import GridSelectorInput from '../../../../components/controls/GridSelectorInput';
import { DisposalReasonGismtSelectorModalSelectorModal } from '../../../Dictionaries/DisposalReasonGismt/components/DisposalReasonGismtSelectorModal';
import { useDisposalReasonGismt } from '../../../../system/hooks/useDisposalReasonGismt';
import { ConfirmationItemsModal } from '../../../../components/modalWindows/ConfirmationItemsModal';
import { useComPortScanner } from '../../../../system/hooks/useHubConnection';
import { KizParsed } from '../../../Dictionaries/Kiz/KizParsed';
import { ILotByIdDTO } from '../../../../libs/core-api-requests/accounting/lotRequest';
import { KizDataProvider } from '../../../../Services/DataProviders/KizDataProvider';
import { useInvoiceItemTabSettings } from '../../Invoice/InvoiceCreator/InvoiceItemCreator/useInvoiceItemTabSettings';
import { SysOptionsDataProvider } from '../../../../Services/DataProviders/SysOptionsDataProvider';

interface IMessageModalProps {
    show: boolean;
    message: string;
    btSaveOrOk?: BtSaveOrOk;
}

interface IReturnCreatedItem {
    displayItem: any;
    createdItem: any;
}

interface RecipeFields {
    recipeNumber: string;
    recipeSeries: string;
    recipeDate: DateTime | null;
}

type BtSaveOrOk = "ok" | "save";

export const InvoiceOutCreator: FC<IDocumentCreatorFormProps<IInvoiceOutGetDTO, IInvoiceOutUpdateDTO>> = (props) => {
    const appContext = useAppContext();
    
    const ldp = new LotDataProvider(appContext.coreApiService);
    const kizDP = new KizDataProvider(appContext.coreApiService)
    const invoiceDP = new InvoiceDataProvider(appContext.coreApiService);
    const sysOptionsDP = new SysOptionsDataProvider(appContext.coreApiService);
    const operativeReserve = useOperativeReserve();
    const userContext = useUserContext();
    const lockFromPermission = checkLockStatus(props.permission as IPermission, userContext.userPermission);
    const [onlyRead, setOnlyRead] = useState((props.variant === 'edit' && (props.document?.documentState === 'proc' || lockFromPermission)) ? true : false);
    const idTable = IdTableVariant.InvoiceOut
    const lockingDocuments = useLockingDocuments();
    const [idLockingDocument, setIdLockingDocument] = useState<string|undefined>();

    const [entity, setEntity] = useState<IInvoiceOutUpdateDTO>({} as IInvoiceOutUpdateDTO);
    const invoiceOut = useInvoiceOutState(props.idGlobal ?? uuidv4(), props.document, (value) => setEntity(value));
    const validator = useInvoiceOutValidator(entity);

    const [selectedItem, setSelectedItem] = useState<IInvoiceOutItemViewDTO>();
    const [viewItems, setViewItems] = useState<IInvoiceOutItemViewDTO[]>(invoiceOut.viewItems(props.document?.items ?? []));
    const [viewItemsGF] = useGridFilter();

    const [kizModal, setKizModal] = useState(<></>);
    const [kizModalVisible, setKizModalVisible] = useState(false);

    const [lotGridFilter, dispatchLotGridFilter] = useGridFilter();
    const [invoiceOutItemModal, setInvoiceOutItemModal] = useState<JSX.Element | null>(null);

    //Validations
    const [itemsValidation, setItemsValidation] = useState<IValidationItem[]>([]);
    const [itemsFilter, setItemsFilter] = useState<IItemFilter[]>([]);

    const [showMessageModalValidDocument, setShowMessageModalValidDocument] = useState<IMessageModalProps>({ show: false, message: "" });
    const [setTimer, clearTimer]  = useTimeout()

    const { t } = useTranslation();
    const baseT = (value: string) => t("documents.invoiceOut." + value);
    const errorsT = (value: string) => t("errors." + value);
    const [showMessageModal, setShowMessageModal] = useState<IMessageModalProps>({ show: false, message: "" });

    const [baseDocument, setBaseDocument] = useState<string>();
    const [showMessageModalValidBaseDocument, setShowMessageModalValidBaseDocument] = useState<boolean>(false);
    const [selectBaseDocument, setSelectBaseDocument] = useState<boolean>(false);

    const scalingRatioDP = new ScalingRatioDataProvider(appContext.coreApiService);
    const [scanData, setScanData] = useState<string>("");
    const [searchOption, setSearchOption] = useState<SearchOption>(SearchOption.goodsName);

    const [recipeFieldsError, setRecipeFieldsError] = useState('');
    const [emptyRecipeFields, setEmptyRecipeFields] = useState<string[]>([]);

    const tabsContext = useTabsContext();
    const currentIdTab = useRef<string>('');

    const roles = (userContext.userRoles || []).flat().map(role => role.trim());

    const lockCreateFutureDocs = checkLockStatus("FutureDocsCreation", userContext.userPermission)
    const isAdministrator = roles.some(role => role.toLowerCase() === 'administrator');

    const hasFullAccessCreateFutureDocs = isAdministrator || !lockCreateFutureDocs;

    const scanner = useComPortScanner((data) => {
        if (kizModalVisible) return
        const decodedText = atob(data);
    
        let kizParsed = new KizParsed(decodedText)
        const encodedGtinSgtin = encodeURIComponent(kizParsed.gtinSgtin);
        const encodedBarcodeBase64 = encodeURIComponent(kizParsed.rawBarcodeCorrectedBase64);
        
        if (kizParsed.isKiz) {
            ldp.getLotByGtinSgtin(encodedGtinSgtin, idTable as number, (lot: ILotByIdDTO) => {
                if (lot && lot.idKizGlobalRequested && lot.idKizGlobalRequested.length > 0 && lot.idKizGlobalRequested != uuidNull) {
                    kizDP.saveRawBarcode(lot.idKizGlobalRequested, encodedBarcodeBase64)
                }

                kizDP.parse(encodedBarcodeBase64, (parsedBarcode) => {
                    setScanData(decodedText)
                    switch(parsedBarcode.barcodeType) {
                        case BarcodeType.Kiz: {
                            const kizData = parsedBarcode.kizData;
                            handleLotSelect(lot, {type: 'kiz', data: {...kizData}} as ISearchQueryInfo,decodedText)                           
                            break;
                        }
                        case BarcodeType.kizOsu: {
                            const kizOsuData = parsedBarcode.kizOsuData;
                            handleLotSelect(lot,{type: 'kiz', data: {...kizOsuData}} as ISearchQueryInfo,kizOsuData.gtinSGtin)
                            break;
                        }
                    }
                })
            })
        }
    })
 
    const renderKizScanModal = () => {
        if (!kizModalVisible) {
            setKizModal(<></>);
        } else {
            if (selectedItem) {
                const editItem = invoiceOut.findItem(selectedItem?.idGlobal);
                const idLotGlobal = editItem?.idLotGlobal;
                if (idLotGlobal) {
                    ldp.getById(idLotGlobal, (data) => {
                        scalingRatioDP.overrideGetById(data.goods.idGlobal, data.scalingRatio.idGlobal, (scalingRatio)=>{
                            setKizModal(
                                <KizScanModal
                                    ok={(value: IInvoiceOutItemEditDTO) => {
                                        invoiceOut.editItem({...value,dateCreated: DateTime.now()});
                                        setKizModalVisible(false);
                                    }}
                                    cancel={() => setKizModalVisible(false)}
                                    selectedItem={editItem as IInvoiceOutItemEditDTO}
                                    documentType={DocumentType.invoiceOut}
                                    document={{ idTable: IdTableVariant.InvoiceOut, idDocument: entity.idGlobal }}
                                    idLotFrom={editItem.idLotGlobal ?? null}
                                    numerator={data.numerator}
                                    scalingRatio={scalingRatio}
                                    gisMtOnlyMode={disposalGismtForUnscannedLotsMode}
                                />
                            );
                        })
                    });
                }
            }
        }
    };
    
    const {getBooleanSetting } = useInvoiceItemTabSettings();
    const showLastSearchQuery = getBooleanSetting('showLastSearchQuery')
    const lastSearchQuerySettings = {
        showLastSearchQuery: showLastSearchQuery,
        searchOption: searchOption,
        documentKey: 'invoiceItemCreator' as DocumentKey,
        fieldName: 'inputAccompanyingSelectionGoods'
    };

    useEffect(() => renderKizScanModal(), [kizModalVisible]);
    useEffect(() => dispatchLotGridFilter(onStoreChanged(invoiceOut.store.value?.idGlobal)), [invoiceOut.store.value]);
    useEffect(() => dispatchLotGridFilter(onPharmacyChanged(invoiceOut.pharmacy.value?.idGlobal)), [invoiceOut.pharmacy.value]);

    useEffect(() => {
        if (entity?.items === undefined) {
            return;
        }
        setViewItems(
            entity.items.map((item) => {
                return {
                    idGlobal: item.idGlobal,
                    lotName: viewItems.find((el) => el.idGlobal === item.idGlobal)?.lotName,
                    quantity: item.quantity,
                    isKiz: item.isKiz,
                    isKizBox: item.isKizBox,
                    goodsCode: item.goodsCode,
                    producer: item.producer,
                    retailPrice: item.retailCostInfo?.price,
                    retailPriceIncVat: item.retailCostInfo?.priceIncVat,
                    retailVat: item.retailCostInfo?.vat,
                    retailVatSum: item.retailCostInfo?.vatSum,
                    retailSum: item.retailCostInfo?.sum,
                    retailSumIncVat: item.retailCostInfo?.sumIncVat,
                    scalingRatioName: item.scalingRatioName,
                    supplierPrice: item.supplierCostInfo?.price,
                    supplierPriceIncVat: item.supplierCostInfo?.priceIncVat,
                    supplierVat: item.supplierCostInfo?.vat,
                    supplierVatSum: item.supplierCostInfo?.vatSum,
                    supplierSum: item.supplierCostInfo?.sum,
                    supplierSumIncVat: item.supplierCostInfo?.sumIncVat,
                    discountPrice: item.discountCostInfo?.price,
                    discountPriceIncVat: item.discountCostInfo?.priceIncVat,
                    discountVat: item.discountCostInfo?.vat,
                    discountVatSum: item.discountCostInfo?.vatSum,
                    discountSum: item.discountCostInfo?.sum,
                    discountSumIncVat: item.discountCostInfo?.sumIncVat,
                    supplier: item.suppler,
                    dateCreated: item.dateCreated,
                    isOsu: item?.isOsu,
                    countKizScan:item?.isOsu ? 1 : (item.kizs?.length ?? 0) + (item.kizBoxes?.length ?? 0) + (item.kizOsuDtos?.length ?? 0),
                    goodsName: item.goodsName,
                    gosContractFundingSourseName: viewItems.find((el) => el.idGlobal === item.idGlobal)?.gosContractFundingSourseName,
                } as IInvoiceOutItemViewDTO;
            })
        );
    }, [entity.items]);

    useEffect(() => {
        if (tabsContext?.currentTab) {
            currentIdTab.current = tabsContext.currentTab?.id;
        }
        const handleTabClose = (event) => {
            lockingDocuments.delete({
                idTable: IdTableVariant.InvoiceOut,
                idDocument: props.idGlobal as string,
            });
            return (event.returnValue = "");
        };

        window.addEventListener("beforeunload", handleTabClose);

        return () => {
            window.removeEventListener("beforeunload", handleTabClose);
        };
    }, []);

    // Блокировка документа
    const requestUpdateLockingDocumentTimeout = useRef<NodeJS.Timeout>();
    const isUpdatingLockingDocument = useRef(false);

    useEffect(() => {
        // Если документ не определён — ничего не делаем
        if (!idLockingDocument) return;

        // Если уже идёт обновление — не ставим новый таймер
        if (isUpdatingLockingDocument.current) return;

        // Отменяем предыдущий таймер
        if (requestUpdateLockingDocumentTimeout.current) {
            clearTimeout(requestUpdateLockingDocumentTimeout.current);
        }

        clearTimeout(requestUpdateLockingDocumentTimeout.current as NodeJS.Timeout);
        requestUpdateLockingDocumentTimeout.current = setTimeout(() => {

            isUpdatingLockingDocument.current = true;

            lockingDocuments.update(idLockingDocument, (e) => {
                isUpdatingLockingDocument.current = false;
            },
                () => {
                    isUpdatingLockingDocument.current = false;
                })
        }, Number(process.env.REACT_APP_INTERVAL_REQUEST));
    }, [entity.documentDate, entity.idContractorToGlobal, entity.idStoreGlobal, entity.idPayerGlobal, entity.items]);


    useEffect(() => {
        if ((props.variant === "edit" || props.variant === "copy") && props.document) {

            if (props.variant === "edit")
                lockingDocuments.check(props.idGlobal as string, (e)=>{
                    if (e) setOnlyRead(true);
                    else
                        lockingDocuments.send({
                            idTable: IdTableVariant.InvoiceOut,
                            idDocument: props.idGlobal as string,
                        }, (e)=> {
                            setIdLockingDocument(e)
                        });
                })

            let arr: IItemFilter[] = [];
            props.document.items?.forEach((x) => {
            ldp.remain(x.lot.idGlobal, props.idGlobal as string, (response) => {
                    arr.push({
                        idItem: x.idGlobal as string,
                        quantityMax: response,
                        quantityMin: 0,
                    });
                    });
                });
            setItemsFilter(arr);
            const propsValidateItems = props.document.items.map(el => ({
                err: false,
                message: "",
                idGlobal: el.idGlobal,
            })) as IValidationItem[]
            setItemsValidation(propsValidateItems)

            if (props.document.baseDocumentInfo?.idTable === IdTableVariant.Invoice) {
                invoiceDP.getById(props.document.baseDocumentInfo.idDocument, (entity) => setBaseDocument(entity.displayName))
            }
        }
    }, [])

    useEffect(() => {
        if (props.document) {
            fieldsRef.current = {
                recipeNumber: props.document.recipeNumber || '',
                recipeSeries: props.document.recipeSeries || '',
                recipeDate: props.document.recipeDate || null
            };            
            
            const { recipeNumber, recipeSeries, recipeDate } = fieldsRef.current;
            const hasValidDate = recipeDate?.isValid === true;
            
            if (recipeNumber || recipeSeries || hasValidDate) {
                const initialMissingRecipeFields: string[] = [];
                
                if (!recipeNumber) initialMissingRecipeFields.push('recipeNumber');
                if (!recipeSeries) initialMissingRecipeFields.push('recipeSeries');
                if (!hasValidDate) initialMissingRecipeFields.push('recipeDate');
                
                if (initialMissingRecipeFields.length > 0) {
                    setEmptyRecipeFields(initialMissingRecipeFields);
                    setRecipeFieldsError(errorsT("recipeFieldsRequired"));
                }
            }
        }
    }, [props.document]);

    const fieldsRef = useRef<RecipeFields>({
        recipeNumber: '',
        recipeSeries: '',
        recipeDate: null
    });

    const handleRecipeFieldChange = (fieldName: keyof RecipeFields, value: any, setter: (value: any) => void) => {
        fieldsRef.current[fieldName] = value;
        setter(value);
        validateRecipeFields();
        setTimeout(() => validateRecipeFields(), 0);
    };
    
    const validateRecipeFields = () => {
        const { recipeNumber, recipeSeries, recipeDate } = fieldsRef.current;
        const hasValidDate = recipeDate?.isValid === true;
        
        if (recipeNumber || recipeSeries || hasValidDate) {
            const missingRecipeFields: string[] = [];

            if (!recipeNumber) missingRecipeFields.push('recipeNumber');
            if (!recipeSeries) missingRecipeFields.push('recipeSeries');
            if (!hasValidDate) missingRecipeFields.push('recipeDate');

            if (missingRecipeFields.length > 0) {
                setEmptyRecipeFields(missingRecipeFields);
                setRecipeFieldsError(errorsT("recipeFieldsRequired"));
                return false;
            }            
        }
        setRecipeFieldsError('');
        return true;
    };

    const validateKiz =(searchQuery?: ISearchQueryInfo ):boolean => {
        if (searchQuery?.type) {
            const kizs = entity.items.flatMap(x => x.kizs) as IKizDTO[]
            const kizBoxes = entity.items.flatMap(x => x.kizBoxes) as IKizBoxDTO[]
            if (!validateDuplicateKizs(entity.items,searchQuery.data.barcode,DocumentType.invoiceOut,kizs,kizBoxes))
            {
                renderGlobalAlert({
                    variant: "exception",
                    detail: `${searchQuery.data.barcode} уже был добавлен ранее в текущем документе`,
                    title: `Уже был добавлен ранее в текущем документе`,
                    statusCode: 500,
                    isSound: true
                });
                return false;
            }
            return true;
            
        }  
        return true;
    }

    const onSelectLot = (lot: ILotDTO, searchQuery?: ISearchQueryInfo, decodedBarcode?: string) => {
        if (!checkStorePharmacy(lot, invoiceOut.store.value, invoiceOut.pharmacy.value)) return;

        const foundItem = invoiceOut.findItemByLot(lot.idGlobal);
        
        if (foundItem) {

            if (!validateKiz(searchQuery)) return
            let updatedItem: IInvoiceOutItemEditDTO = { ...foundItem, quantity: foundItem.quantity + 1 };
            switch (searchQuery?.type) {
                case "kiz": {
                    updatedItem = updatedItem.isPieceAccountingKizOsu
                        ? {
                            ...updatedItem,
                            kizOsuDtos: [
                                ...(updatedItem.kizOsuDtos ?? []),
                                searchQuery.data,
                            ],
                        }
                        : {
                            ...updatedItem,
                            kizs: [
                                ...(updatedItem.kizs ?? []),
                                searchQuery.data,
                            ],
                        };
                    break;
                }
                case "kizBox":
                    updatedItem = {
                        ...updatedItem,
                        kizBoxes: [
                            ...(updatedItem.kizBoxes ?? []),
                            { ...searchQuery.data, quantity: lot.numerator },
                        ],
                    };
                    break;
            }
            
            const totalKizCount = (updatedItem.kizs?.length ?? 0) + (updatedItem.kizBoxes?.length ?? 0) + (updatedItem.kizOsuDtos?.length ?? 0);
            if (updatedItem.quantity > totalKizCount && totalKizCount > 0) {
                updatedItem = {...updatedItem, quantity: foundItem.quantity}
            }

            const lotId = updatedItem.idLotGlobal;

            if (!lotId) {
                return;
            }

            ldp.getById(lotId, (lotData) => {
                const costs = calculateCosts(
                    lotData.retailCostInfo,
                    lotData.supplierCostInfo,
                    updatedItem?.quantity ?? 0,
                    updatedItem?.discountCostInfo?.vat ?? 0
                );

                invoiceOut.editItem({
                    ...updatedItem,
                    ...costs,
                });
            });

    
            return;
        }

        openChild(
            uuidv4(),
            (edit: IInvoiceOutItemEditDTO, view: IInvoiceOutItemViewDTO) => {
                const lotId = edit.idLotGlobal;

                if (!lotId) {
                    return;
                }
                
                ldp.getById(edit.idLotGlobal, (lot) => {

                    const costs = calculateCosts(
                        lot.retailCostInfo,
                        lot.supplierCostInfo,
                        edit.quantity,
                        edit.discountCostInfo?.vat ?? 0
                    );
        
                    const finalItem = {
                        ...edit,
                        ...costs,
                    };
        
                    invoiceOut.addItem(finalItem);
        
                    setViewItems((prev) => [
                        ...prev,
                        {
                            ...view,
                            discountPrice: finalItem.discountCostInfo?.price ?? 0,
                            discountPriceIncVat: finalItem.discountCostInfo?.priceIncVat ?? 0,
                            discountVat: finalItem.discountCostInfo?.vat ?? 0,
                            discountVatSum: finalItem.discountCostInfo?.vatSum ?? 0,
                            discountSum: finalItem.discountCostInfo?.sum ?? 0,
                            discountSumIncVat: finalItem.discountCostInfo?.sumIncVat ?? 0,
                        },
                    ]);
                    
                    invoiceOut.storeFromLot.setValue(lot.store);
                    setInvoiceOutItemModal(null);
                });
            },
            () => setInvoiceOutItemModal(null),
            {
                idInvoiceOutGlobal: props.idGlobal,
                lot: lot,
                scanData: decodedBarcode ? decodedBarcode : scanData,
                isWithdrawalGismtForUnscannedLots: disposalGismtForUnscannedLotsMode
            } as IInvoiceOutItemActionDataTab
        );
    };

    const openChild = (idGlobal: string, ok: (item: IInvoiceOutItemEditDTO, displayItem: IInvoiceOutItemViewDTO) => void, cancel: () => void, data: IInvoiceOutItemActionDataTab) => {
        setInvoiceOutItemModal(InvoiceOutItemCreatorTab(idGlobal, ok, cancel, data));
    };

    const calculateCosts = (lotRetailCost: ICostInfoDTO, lotSupplierCost: ICostInfoDTO, quantity: number, discountPercent: number): {
        retailCostInfo: ICostInfoDTO;
        discountCostInfo: ICostInfoDTO;
        supplierCostInfo: ICostInfoDTO
    } => {
        const roundToTwo = (value: number): number => Number(value.toFixed(2));
    
        const oldPriceIncVat = lotRetailCost.priceIncVat ?? 0;
        const oldVat = lotRetailCost.vat ?? 0;
        const oldPrice = lotRetailCost.price ?? 0;
    
        const priceIncVatValue = oldPriceIncVat - (oldPriceIncVat / 100) * discountPercent;
        const priceValue = priceIncVatValue / (1 + oldVat / 100);
        const vatValue = (priceValue / 100) * oldVat;
        
        const retailCostInfo: ICostInfoDTO = {
            price: roundMoneyFormat(priceValue),
            vatPrice: roundMoneyFormat(priceIncVatValue) - roundMoneyFormat(priceValue),
            priceIncVat: roundMoneyFormat(priceIncVatValue),
            vat: oldVat,
            vatSum: roundMoneyFormat(vatValue * quantity),
            sum: roundMoneyFormat((roundMoneyFormat(priceIncVatValue) * quantity) / (oldVat / 100 + 1)),
            sumIncVat: roundMoneyFormat(priceIncVatValue) * quantity,
            adprice: 0,
        };
    
        const priceDiscount = roundMoneyFormat(oldPrice - retailCostInfo.price);
        const priceIncVatDiscount = roundMoneyFormat(oldPriceIncVat - retailCostInfo.priceIncVat);
        const priceWithQuantityDiscount = priceDiscount * quantity;
        const priceIncVatWithQuantityDiscount = priceIncVatDiscount * quantity;
    
        const discountCostInfo: ICostInfoDTO = {
            price: priceDiscount,
            vatPrice: priceIncVatDiscount - priceDiscount,
            priceIncVat: priceIncVatDiscount,
            vat: discountPercent,
            vatSum: roundMoneyFormat(priceIncVatWithQuantityDiscount - priceWithQuantityDiscount),
            sum: roundToTwo(priceWithQuantityDiscount),
            sumIncVat: roundToTwo(priceIncVatWithQuantityDiscount),
            adprice: 0,
        };

        const priceSupplier = lotSupplierCost.price ?? 0;
        const priceIncVatSupplier = lotSupplierCost.priceIncVat ?? 0;
        const vat = lotSupplierCost.vat ?? 0;

        const supplierCostInfo: ICostInfoDTO = {
            price: priceSupplier,
            priceIncVat: priceIncVatSupplier,
            vat: vat,
            vatPrice: roundMoneyFormat(priceIncVatSupplier - priceSupplier),
            sum: roundMoneyFormat(priceSupplier * quantity),
            sumIncVat: roundMoneyFormat(priceIncVatSupplier * quantity),
            vatSum: roundMoneyFormat((priceIncVatSupplier - priceSupplier) * quantity),
            adprice: lotSupplierCost.adprice ?? 0,
        }

        return {retailCostInfo, supplierCostInfo, discountCostInfo};
    };

    const onDiscountChanged = (
        quantity: number,
        item: IInvoiceOutItemEditDTO,
        newValue: number
    ) => {
        if (!item.idLotGlobal) return;
    
        ldp.getById(item.idLotGlobal, (lot) => {
            const costs = calculateCosts(
                lot.retailCostInfo,
                lot.supplierCostInfo,
                quantity,
                newValue
            );
    
            invoiceOut.editItem({
                ...item,
                quantity,
                ...costs,
            });
        });
    };

    const onQuantityChanged = (item: IInvoiceOutItemEditDTO, newValue: number) => {
        if (item.idLotGlobal === undefined) {
            return;
        }

        onDiscountChanged(newValue, item, roundMoneyFormat(item.discountCostInfo?.vat));
    };

    const onRetailPriceIncVatChanged = (item: IInvoiceOutItemEditDTO, newValue: number) => {
        if (item.idLotGlobal === undefined) {
            return;
        }

        ldp.getById(item.idLotGlobal, (data) => {
            const roundToTwo = (value: number): number => Number(value.toFixed(2));
            const oldPriceIncVat = data.retailCostInfo?.priceIncVat ?? 0;
            const oldVat = data.retailCostInfo?.vat ?? 0;
            const oldPrice = data.retailCostInfo?.price ?? 0;

            let newDiscount = 100 - (newValue * 100) / oldPriceIncVat;
            newDiscount = roundMoneyFormat(newDiscount);

            const priceValue = newValue / (1 + oldVat / 100);
            const vatValue = (priceValue / 100) * oldVat;

            const retailCostInfo: ICostInfoDTO = {
                price: roundMoneyFormat(priceValue),
                vatPrice: roundMoneyFormat(newValue) - roundMoneyFormat(priceValue),
                priceIncVat: roundMoneyFormat(newValue),
                vat: oldVat,
                vatSum: roundMoneyFormat(vatValue * item.quantity),
                sum: roundMoneyFormat((roundMoneyFormat(newValue) * item.quantity) / (oldVat / 100 + 1)),
                sumIncVat: roundMoneyFormat(newValue) * item.quantity,
                adprice: 0,
            };

            const priceDiscount = roundMoneyFormat(oldPrice - retailCostInfo.price);
            const priceIncVatDiscount = roundMoneyFormat(oldPriceIncVat - retailCostInfo.priceIncVat);
            const priceWithQuantityDiscount = priceDiscount * item.quantity;
            const priceIncVatWithQuantityDiscount = priceIncVatDiscount * item.quantity;

            const discountCostInfo: ICostInfoDTO = {
                price: priceDiscount,
                vatPrice: priceIncVatDiscount - priceDiscount,
                priceIncVat: priceIncVatDiscount,
                vat: newDiscount,
                vatSum: roundMoneyFormat((priceIncVatWithQuantityDiscount ?? 0) - (priceWithQuantityDiscount ?? 0)),
                sum: roundToTwo(priceWithQuantityDiscount),
                sumIncVat: roundToTwo(priceIncVatWithQuantityDiscount),
                adprice: 0,
            };
            
            invoiceOut.editItem({
                ...item,
                retailCostInfo: { ...retailCostInfo },
                discountCostInfo: { ...discountCostInfo },
            });
        });
    };

    const onChangeData = (value: IChangeDataGrid) => {
        const item = entity.items.find((x) => x.idGlobal === value.idGlobal);

        if (item === undefined) {
            return;
        }

        switch (value.propertyName) {
            case "discountVat":
                onDiscountChanged(item.quantity, item, value.value as number);
                break; //скидка
            case "retailPriceIncVat":
                onRetailPriceIncVatChanged(item, value.value as number);
                break; //цена розничная
            case "quantity":
                onQuantityChanged(item, value.value as number);
                break; //количество
        }
    };

    const onCancel = () => {
        operativeReserve.deleteAll(
            {
                idTable: IdTableVariant.InvoiceOut,
                idDocument: entity.idGlobal as string,
            },
            () => props.cancel()
        );

        lockingDocuments.delete({
            idTable: IdTableVariant.InvoiceOut,
            idDocument: entity.idGlobal as string,
        });

        scanner.disconnect();
    };

    const isValidDocument = (items:IInvoiceOutItemEditDTO[], showMessage: () => void): boolean => {
        var flag = true;
        if (!items || items.length === 0)
        {
            flag = false;
            showMessage();
        }
        return flag;
    }

    const [сonfirmationItemsModalJsx, setConfirmationItemsModalJsx] = useState<JSX.Element>(<></>);
    const renderConfirmationModal = (header: string, warning: string, items: string, question: string, ok: () => void, cancel: () => void) => {
        setConfirmationItemsModalJsx(
            <ConfirmationItemsModal
                header={`${header}`}
                warning={`${warning}`}
                listPositions={`${items}`}
                question={`${question}`}
                ok={ok}
                cancel={cancel}
            />
        );
    }
    
    const confirmSave = (save: (model: IInvoiceOutUpdateDTO) => void) => {
        const hasGismtItemsWithoutReason = !disposalReasonGismt && entity.items.some(item =>
            item.kizOsuDtos && item.kizOsuDtos.length > 0
        );

        sysOptionsDP.getByName(SysOptionName.ValidateInfoGismt, (e) => {
            if (hasGismtItemsWithoutReason && e?.value === 'true') {

                const gismtItems = entity.items.filter(item => item.kizOsuDtos && item.kizOsuDtos.length > 0).map(item => {
                    const displayItem = viewItems.find(x => x.idGlobal === item.idGlobal);
                    return displayItem?.lotName || `Код товара '${item.goodsCode}'`;
                }).join(', ');

                renderConfirmationModal(
                    "Подтверждение сохранения",
                    "В документе присутствуют товары ГИС МТ без указанной причины выбытия:",
                    gismtItems,
                    "Вы уверены, что хотите продолжить без указания причины выбытия?",
                    () => {
                        save(entity)
                        setConfirmationItemsModalJsx(<></>)
                    },
                    () => setConfirmationItemsModalJsx(<></>)
                );
            } else {
                // Если причина выбрана или нет ГИС МТ товаров - сразу сохраняем
                save(entity);
            }
        })

        scanner.disconnect();
    }

    const onOkAction = () => {
        lockingDocuments.delete({
            idTable: IdTableVariant.InvoiceOut,
            idDocument: entity.idGlobal as string,
        });

        confirmSave(props.ok);
    };
    
    const isDisabledEditPanel = () => {
        const item = invoiceOut.findItem(selectedItem?.idGlobal ?? "")
        if ((onlyRead) || !((item?.isKiz) || (item?.isOsu))) return true
        return false
    }
    // Функция для генерация editEntity и view;
    function transformArray(lots: ILotDTO[]) {
        const edit: IInvoiceOutItemEditDTO[] = [];
        const view: IInvoiceOutItemViewDTO[] = [];

        lots.forEach(lot => {
            const quantity = 1
            const discount = 0

            const costs = calculateCosts(
                lot.retailCostInfo,
                lot.supplierCostInfo,
                quantity,
                discount
            );
            
            const editEntity: IInvoiceOutItemEditDTO = {
                idGlobal: uuidv4(),
                idLotGlobal: lot?.idGlobal,
                idGoodsGlobal: lot?.goods?.idGlobal,
                quantity: 1,
                retailCostInfo: costs.retailCostInfo,
                scalingRatioName: lot?.scalingRatio.displayName,
                supplierCostInfo: costs.supplierCostInfo,
                discountCostInfo: costs.discountCostInfo,
                isKiz: lot.isKiz,
                isPieceAccountingKizOsu: lot.isPieceAccountingKizOsu,
                kizs: undefined,
                goodsCode: lot.goodsCode,
                producer: lot.producer.displayName,
                kizBoxes: undefined,
                isKizBox: lot.isKizBox,
                kizOsuDto: undefined,
                isOsu: undefined,
                suppler: lot?.supplier ?? '',
                goodsName: lot.goods.displayName
            } as IInvoiceOutItemEditDTO;

            const viewEntity: IInvoiceOutItemViewDTO = {
                idGlobal: editEntity.idGlobal,
                lotName: lot.displayName,
                quantity: editEntity.quantity,

                retailPrice: editEntity.retailCostInfo?.price,
                retailPriceIncVat: editEntity.retailCostInfo?.priceIncVat,
                retailVat: editEntity.retailCostInfo?.vat,
                retailSum: editEntity.retailCostInfo?.sum,
                retailVatSum: editEntity.retailCostInfo?.vatSum,
                retailSumIncVat: editEntity.retailCostInfo?.sumIncVat,

                scalingRatioName: editEntity.scalingRatioName,
                supplierPrice: editEntity.supplierCostInfo?.price,
                supplierPriceIncVat: editEntity.supplierCostInfo?.priceIncVat,
                supplierVat: editEntity.supplierCostInfo?.vat,
                supplierSum: editEntity.supplierCostInfo?.sum,
                supplierVatSum: editEntity.supplierCostInfo?.vatSum,
                supplierSumIncVat: editEntity.supplierCostInfo?.sumIncVat,

                discountPrice: 0,
                discountPriceIncVat: 0,
                discountVat: 0,
                discountSum: 0,
                discountVatSum: 0,
                discountSumIncVat: 0,

                isOsu: editEntity?.isOsu,
                supplier: editEntity.suppler,
                countKizScan: 0,
                isKiz: editEntity.isKiz ?? false,
                isKizBox: editEntity.isKizBox ?? false,

                goodsCode: lot.goodsCode,
                producer: lot.producer.displayName,
                goodsName: lot.goods.displayName,

            } as IInvoiceOutItemViewDTO;

            edit.push(editEntity);
            view.push(viewEntity);
        });

        return { edit, view };
    }

    
    const onSelectLots = (lots: ILotDTO[]) => {
        //TODO Избавление от дублей при добавлении позиций.
        const existingIds = new Set(entity?.items.map(item => item.idLotGlobal));
        const uniqueRows = lots.filter(lot => !existingIds.has(lot.idGlobal))
        //TODO Выходим из функции если нету уникальных позиций.
        if (uniqueRows.length === 0) {
            return;
        }        
        //TODO Добавление только 1 позиции если не указан "Склад поставщика".
        if (!invoiceOut.store?.value) {
            const currentStore = uniqueRows[0].store;
            const uniqueFirstRow = uniqueRows.slice(0, 1);

            invoiceOut.storeFromLot.setValue(currentStore ?? '');//Добавление склада поставщика.
            const { edit, view } = transformArray(uniqueFirstRow);

            const editLot:IInvoiceOutItemEditDTO = edit[0];
            invoiceOut.addItem(editLot);

            setItemsValidation([...itemsValidation, { err: false, message: "", idGlobal: editLot.idGlobal }]);
            ldp.remain(editLot.idLotGlobal as string, props.idGlobal as string, (response) => {
                setItemsFilter([
                    ...itemsFilter,
                    {
                        idItem: editLot.idGlobal,
                        quantityMax: response,
                        quantityMin: 0,
                    },
                ]);
            });

            setViewItems((prevViewItems) => [...prevViewItems, ...view]); // Обычное отображение ВЬЮХИ
            return;
        }

        //TODO Добавление всех уникальных позиций.
        const { edit, view } = transformArray([...uniqueRows]);
        //Добавление entity + онлайн резерв.
        invoiceOut.addItems(edit);
        //Функция для получения остатков и добавления валидации в таблице.
        const filterAndValidateItems = async (edit) => {
            try {
                const newItemsValidation: IValidationItem[] = [];
                const promises = edit.map((item) => {
                    newItemsValidation.push({ err: false, message: "", idGlobal: item.idGlobal } as IValidationItem);

                    return new Promise<IItemFilter>((resolve) =>
                        ldp.remain(item.idLotGlobal as string, props.idGlobal as string, (response) => {
                            if (response !== undefined) {
                                resolve({
                                    idItem: item.idGlobal,
                                    quantityMax: response,
                                    quantityMin: 0,
                                });
                            }
                        })
                    );
                });
                const newItemsFilter = await Promise.all(promises);

                setItemsValidation((prev) => [...prev, ...newItemsValidation]);
                setItemsFilter((prev) => [...prev, ...newItemsFilter]);
            } catch (error) {
                console.error("Ошибка", error);
            }
        };
        filterAndValidateItems(edit);
        setViewItems((prevViewItems) => [...prevViewItems, ...view]);
    }
    
    const {
        disposalReasonGismt,
        disposalReasonGismtEnabled,
        disposalGismtForUnscannedLotsMode,
        disposalGismtForUnscannedLotsModeEnabled,
        updateDisposalReasonGismt,
        onclearDisposalReasonGismt,
        updateDisposalGismtForUnscannedLotsMode
    } = useDisposalReasonGismt<IInvoiceOutUpdateDTO>({
        data: entity,
        setData: setEntity,
        getKizs: (d) => d.items?.flatMap(x => x.kizOsuDtos) as IKizDTO[] ?? [], // flatMap специально для правильной глубины фильтра с учетом каждой строки документа
        getIdDisposalReasonGismtGlobal: (d) => d.idDisposalReasonGismtGlobal,
        getIsWithdrawalGismtForUnscannedLots: (d) => d.isWithdrawalGismtForUnscannedLots
    });

    useEffect(() => {
        // В режиме выбытия ГИС МТ без подтверждения
        // фильтр только для целых партий
        if (disposalGismtForUnscannedLotsMode) {
            dispatchLotGridFilter({
                type: "addColumnFilter",
                payload: {
                    name: "numerator",
                    operator: "Eq",
                    value: 1
                }
            })
            dispatchLotGridFilter({
                type: "addColumnFilter",
                payload: {
                    name: "denominator",
                    operator: "Eq",
                    value: 1
                }
            })
        } else {
            dispatchLotGridFilter({
                type: "deleteColumnFilter",
                payload: "numerator"
            })
            dispatchLotGridFilter({
                type: "deleteColumnFilter",
                payload: "denominator"
            })
        }
    }, [disposalGismtForUnscannedLotsMode])

    const handleLotSelect = (lot, searchQuery, decodedBarcode) => {
        if (Array.isArray(lot)) {
            onSelectLots(lot);
            return;
        }

        if (disposalGismtForUnscannedLotsMode && lot.numerator !== lot.denominator) {
            renderGlobalAlert({
                variant: "error",
                statusCode: 500,
                title: "В режиме выбытия ГИС МТ для неподтвержденных КИЗ допускаются только целые партии",
                detail: `Партия: ${lot.displayName}, ${lot.numerator}, ${lot.denominator}`,
                instance: "Ошибка",
                isSound: true
            });
            return;
        }

        onSelectLot(lot, searchQuery, decodedBarcode);
    };

    return (
        <>
            <BaseDocumentCreator
                valid={true}
                ok={{
                    onClick: () => {
                        const isRecipeValid = validateRecipeFields();
                        if (!isRecipeValid) {
                            setRecipeFieldsError(errorsT("recipeFieldsRequired"));                            
                            return;
                        }
                        if (validator.isValid() && 
                            isValidDocument(entity.items, () => setShowMessageModalValidDocument({ message: errorsT("validDocument"), show: true })) === true &&
                            kizCounter(entity.items, () => setShowMessageModal({ message: errorsT("kizCountNotMatch"), show: true, btSaveOrOk: "ok" }), DocumentType.invoiceOut) === true
                        ) {
                            onOkAction();
                        }
                    },
                    disabled: onlyRead || !!itemsValidation.find((x) => x.err == true) || props.isSubmitting,
                    sendRequestGlobal: props.isSubmitting
                }}
                save={{
                    onClick: () => {
                        const isRecipeValid = validateRecipeFields();
                        if (!isRecipeValid) {
                            setRecipeFieldsError(errorsT("recipeFieldsRequired"));                           
                            return;
                        }
                        if (
                            validator.isValid() && 
                            isValidDocument(entity.items, () => setShowMessageModalValidDocument({ message: errorsT("validDocument"), show: true })) === true &&
                            kizCounter(entity.items, () => setShowMessageModal({ message: errorsT("kizCountNotMatch"), show: true, btSaveOrOk: "save" }), DocumentType.invoiceOut) === true
                        ) {
                            confirmSave(props.save);
                        }
                    },
                    disabled: onlyRead || !!itemsValidation.find((x) => x.err == true) || props.isSubmitting,
                    sendRequestGlobal: props.isSubmitting
                }}
                cancel={{ onClick: onCancel }}
            >
                <div className={styles.container}>
                    <div className={styles.wrapper}>
                        <div>
                            <GridWrapper cols={4}>
                                <StoreSelectorPanel
                                    we
                                    idTable={idTable}
                                    required
                                    compactMode={true}
                                    change={onCanChangeData(viewItems.length)}
                                    storeFromLot={invoiceOut.storeFromLot.value ?? undefined}
                                    contractor={{
                                        label: baseT("pharmacySupplier"), //"Аптека поставщик",
                                        value: invoiceOut.pharmacy.value,
                                        onSelect: (value) => value && invoiceOut.pharmacy.setValue(value),
                                        onClear: () => invoiceOut.pharmacy.setValue(null),
                                        disabled: onlyRead,
                                        treeViewCheckDirectoryType:'pharmacy'
                                    }}
                                    store={{
                                        label: baseT("storeSupplier"), //"Склад поставщика",
                                        value: invoiceOut.store.value,
                                        onSelect: (value) => value && invoiceOut.store.setValue(value),
                                        onClear: () => invoiceOut.store.setValue(null),
                                        error: validator.errors.idStoreGlobal,
                                        onFocus: () => validator.setErrors({ ...validator.errors, idStoreGlobal: undefined }),
                                        disabled: onlyRead,
                                        className: styles.defaultMargin
                                    }}
                                    notAutoComplete={props.variant === "edit" || props.variant === "copy"}
                                    documentStore={invoiceOut.store.value ?? undefined}
                                />
                                <TextInput key="mnemocode" label={baseT("mnemocode")} value={props.document?.mnemocode} disabled />
                                <DateInput 
                                    required 
                                    disabled={onlyRead}
                                    label={baseT("date")}
                                    value={invoiceOut.documentDate.value} 
                                    onBlur={(value) => value && invoiceOut.documentDate.setValue(value)} 
                                    error={validator.errors.documentDate}
                                    preventFutureDates ={!hasFullAccessCreateFutureDocs}
                                />
                            </GridWrapper>
                            <GridWrapper cols={4}>
                                <ContractorSelectorInput
                                    required
                                    disabled={onlyRead}
                                    label={baseT("contractorTo")}
                                    selectedEntity={invoiceOut.contractorTo.value}
                                    onSelect={(value) => invoiceOut.contractorTo.setValue(value)}
                                    onClear={() => invoiceOut.contractorTo.setValue(null)}
                                    error={validator.errors.idContractorToGlobal}
                                    onFocus={() => validator.setErrors({ ...validator.errors, idContractorToGlobal: undefined })}
                                />
                                <ContractorSelectorInput
                                    required
                                    disabled={onlyRead}
                                    label={baseT("buyer")}
                                    selectedEntity={invoiceOut.payer.value}
                                    onSelect={(value) => {
                                        invoiceOut.payer.setValue(value);
                                        invoiceOut.contractorTo.setValue(value);
                                    }}
                                    onClear={() => invoiceOut.payer.setValue(null)}
                                    error={validator.errors.idPayerGlobal}
                                    onFocus={() => validator.setErrors({ ...validator.errors, idPayerGlobal: undefined })}
                                />
                                <DictionaryInput 
                                    placeholder="-- Не выбрано --" 
                                    value={baseDocument} 
                                    label={t("documents.movement.baseDocument")}
                                    onMoreClick={() => {
                                        !invoiceOut?.store?.value?.idGlobal ? setShowMessageModalValidBaseDocument(true) : setSelectBaseDocument(true);
                                    }}
                                    disabled={onlyRead}
                                />
                                <CheckBox
                                    id="isAgentContract"
                                    label={baseT("agentContract")}
                                    defaultChecked={invoiceOut.isAgentContract.value ?? false}
                                    onChanged={(cheque) => invoiceOut.isAgentContract.setValue(cheque)}
                                    disabled={onlyRead}
                                />
                            </GridWrapper>
                            <GridWrapper cols={4}>
                                <TextInput
                                    key="recipeNumber"
                                    label={baseT("recipeNumber")}
                                    value={invoiceOut.recipeNumber.value || ''}
                                    onChange={(value) => handleRecipeFieldChange('recipeNumber', value, invoiceOut.recipeNumber.setValue)}
                                    disabled={onlyRead}
                                    error={emptyRecipeFields.includes('recipeNumber') ? recipeFieldsError : ''}
                                />                               
                                <TextInput
                                    key="recipeSeries"
                                    label={baseT("recipeSeries")}
                                    value={invoiceOut.recipeSeries.value || ''}
                                    onChange={(value) => handleRecipeFieldChange('recipeSeries', value, invoiceOut.recipeSeries.setValue)}
                                    disabled={onlyRead}
                                    error={emptyRecipeFields.includes('recipeSeries') ? recipeFieldsError : ''}
                                />
                                <DateInput                                    
                                    disabled={onlyRead}
                                    label={baseT("recipeDate")}
                                    value={invoiceOut.recipeDate.value}
                                    onBlur={(value) => handleRecipeFieldChange('recipeDate', value, invoiceOut.recipeDate.setValue)}
                                    error={emptyRecipeFields.includes('recipeDate') ? recipeFieldsError : ''}
                                />
                            </GridWrapper>
                            <Accordion opened={false} caption={t("menuGroupNames.directory.additionally")} id={`accordionAdditionally-${uuidv4()}`}>
                                <GridWrapper cols={5}>
                                    <DateInput 
                                        label={baseT("dateShipment")}
                                        value={invoiceOut.dateShipment.value} 
                                        onBlur={(value) => value && invoiceOut.dateShipment.setValue(value)}
                                        disabled={onlyRead}
                                    />
                                    <DateInput 
                                        label={baseT("datePayment")}
                                        value={invoiceOut.datePayment.value} 
                                        onBlur={(value) => value && invoiceOut.datePayment.setValue(value)}
                                        disabled={onlyRead}
                                    />
                                    <SelectControl
                                        disabled={onlyRead}
                                        label={baseT("shipmentType")}
                                        options={[
                                            {
                                                value: 2,
                                                displayName: "Самовывоз",
                                            },
                                            {
                                                value: 3,
                                                displayName: "Доставка",
                                            },
                                        ]}
                                        onSelect={(option) => invoiceOut.shipmentType.setValue(option.value)}
                                    />
                                    <GridSelectorInput
                                        selectorModalJsx={DisposalReasonGismtSelectorModalSelectorModal}
                                        id={"CreateMovementPharmacy"}
                                        label={t("documents.movement.disposalReasonGismt")}
                                        selectedEntity={disposalReasonGismt}
                                        onSelect={(value: IEntitySimpleDTO) => {
                                            updateDisposalReasonGismt(value, invoiceOut.idDisposalReasonGismtGlobal.setValue)
                                        }}
                                        onClear={onclearDisposalReasonGismt}
                                        error={validator.errors.disposalReasonGismt}
                                        disabled={!disposalReasonGismtEnabled || onlyRead}
                                        clues={[t("documents.movement.clueTextDisposalReasonGismt")]}
                                    />
                                    <CheckBox
                                        defaultChecked={disposalGismtForUnscannedLotsMode}
                                        onChanged={(value) => {
                                            invoiceOut.isWithdrawalGismtForUnscannedLots.setValue(value);
                                            updateDisposalGismtForUnscannedLotsMode(value)
                                        }}
                                        label={t("documents.movement.disposalGismtForUnscannedLots")}
                                        disabled={!disposalGismtForUnscannedLotsModeEnabled || onlyRead}
                                        clues={[t("documents.movement.clueTextDisposalGismtForUnscannedLots")]}
                                    />
                                </GridWrapper>
                            </Accordion>
                            <Accordion opened={false} caption={baseT("comment")} id={`accordionComments-${uuidv4()}`}>
                                <GridWrapper cols={1}>
                                    <TextAreaInput 
                                        label={baseT("comment")}
                                        value={invoiceOut.comment.value ?? ""} 
                                        onChange={(value) => invoiceOut.comment.setValue(value)}
                                        disabled={onlyRead}
                                    />
                                </GridWrapper>
                            </Accordion>
                            <FlexRow className={styles.lotPanel}>
                                <PositionEditPanel
                                    kiz={{
                                        onClick: () => setKizModalVisible(true),
                                        disabled: isDisabledEditPanel(),
                                    }}
                                    edit={{
                                        onClick: () => {
                                            const editEntity = entity.items.find((i) => i.idGlobal === selectedItem?.idGlobal);
                                            if (editEntity && editEntity.idLotGlobal) {
                                                ldp.getById(editEntity.idLotGlobal, (value) => {
                                                    openChild(
                                                        editEntity.idGlobal,
                                                        (edit: IInvoiceOutItemEditDTO, view: IInvoiceOutItemViewDTO) => {
                                                            invoiceOut.editItem({...edit, dateCreated: DateTime.now()});
                                                            setViewItems([...viewItems.filter((i) => i.idGlobal !== editEntity.idGlobal), {...view, dateCreated: DateTime.now()}]);
                                                            setInvoiceOutItemModal(null);
                                                        },
                                                        () => setInvoiceOutItemModal(null),
                                                        {
                                                            idInvoiceOutGlobal: props.idGlobal,
                                                            lot: value,
                                                            edit: editEntity,
                                                            isWithdrawalGismtForUnscannedLots: disposalGismtForUnscannedLotsMode
                                                        } as IInvoiceOutItemActionDataTab
                                                    );
                                                });
                                            }
                                        },
                                        disabled: selectedItem && onlyRead,
                                    }}
                                    delete={{
                                        onClick: () => {
                                            if (selectedItem && selectedItem?.idGlobal) {
                                                invoiceOut.deleteItem(selectedItem?.idGlobal, (idGlobal) => {
                                                    setViewItems([...viewItems.filter((i) => i.idGlobal !== selectedItem.idGlobal)]);

                                                    const itemsValid = itemsValidation.filter((item) => item.idGlobal !== idGlobal);
                                                    const itemsFilterValid = itemsFilter.filter((item) => item.idItem !== idGlobal);
                                                    setItemsValidation(itemsValid)
                                                    setItemsFilter(itemsFilterValid)
                                                });
                                            }
                                        },
                                        disabled: selectedItem && onlyRead,
                                    }}
                                />
                                <LotSelectorPanel
                                    barcodeScan={tabsContext.currentTab && tabsContext.currentTab?.id === currentIdTab.current && !kizModalVisible}
                                    disabled={onlyRead}
                                    gridFilter={lotGridFilter}
                                    dispatchGridFilter={dispatchLotGridFilter}
                                    onSelect={(lot, searchQuery,decodedBarcode) => {
                                        handleLotSelect(lot,searchQuery,decodedBarcode)
                                    }}
                                    setScanData={setScanData}
                                    setSearchOption={setSearchOption}
                                    documentIdTable={idTable}
                                    activateGroupProcessing //групповая обработка
                                    lastSearchQuerySettings = {lastSearchQuerySettings}
                                    autoFocus={showLastSearchQuery}
                                    key={selectedItem?.idGlobal}
                                    idStoreGlobal={entity.idStoreGlobal}
                                />
                            </FlexRow>
                            <ChangeableGrid
                                maxDiscountValue={{
                                    'discountVat': 100,
                                }}
                                documentStateProc={onlyRead}
                                itemsFilter={itemsFilter}
                                itemsValidation={itemsValidation}
                                setItemsValidation={(value) => {
                                    const newItemsValidation = itemsValidation.map((item) => (item.idGlobal === value[0].idGlobal ? { ...item, ...value } : item));
                                    setItemsValidation(newItemsValidation);
                                }}
                                gridId={uuidv4()}
                                data={viewItems}
                                documentType={DocumentType.invoiceOut}
                                hiddenPagination={undefined}
                                autoSelect
                                filter={viewItemsGF}
                                totalCount={viewItems.length}
                                onSelect={(row) => setSelectedItem(viewItems.find((i) => i.idGlobal === row?.idGlobal))}
                                plugin={InvoiceOutItemGridPluginSettings}
                                onDoubleClick={(row) => props.onDoubleClick?.(row)}
                                onSort={(i) => dispatchLotGridFilter({ type: "sort", payload: i.propertyName })}
                                onFilterDelete={(i) => dispatchLotGridFilter({ type: "deleteColumnFilter", payload: i.propertyName })}
                                onPageNumberChange={(n) => dispatchLotGridFilter({ type: "changePageNumber", payload: { pageNumber: n } })}
                                onNumberPerPageChange={(n) => dispatchLotGridFilter({ type: "changeNumberPerPage", payload: { numberPerPage: n } })}
                                onColumnEnter={(value: IChangeDataGrid) => clearTimer(() => onChangeData(value))}
                                onChangeData={(value: IChangeDataGrid) => setTimer(() => onChangeData(value))}
                                searching
                            />
                        </div>
                        <Accordion opened={false} caption="" id={`accompanyingInvoiceInfo-${uuidv4()}`} bold documentSticky> {/* TODO: Локализация */}
                            <GridWrapper cols={3}>
                                <MoneyFormatComponent inline disabled label="Сумма НДС, розн." value={entity.retailCostInfo?.vatSum} />
                                <MoneyFormatComponent inline disabled label="Сумма НДС, пост." value={entity.supplierCostInfo?.vatSum} />
                                <MoneyFormatComponent inline disabled label="Сумма НДС, скид." value={entity.discountCostInfo?.vatSum} />
                            </GridWrapper>
                            <GridWrapper cols={3}>
                                <MoneyFormatComponent inline disabled label="Сумма с НДС, розн." value={entity.retailCostInfo?.sumIncVat} />
                                <MoneyFormatComponent inline disabled label="Сумма с НДС, пост." value={entity.supplierCostInfo?.sumIncVat} />
                                <MoneyFormatComponent inline disabled label="Сумма с НДС, скид." value={entity.discountCostInfo?.sumIncVat} />
                            </GridWrapper>
                        </Accordion>
                    </div>
                </div>

                {kizModal}

                {invoiceOutItemModal}

                {showMessageModalValidDocument.show && (
                    <MessageModalWindow
                        message={showMessageModalValidDocument.message}
                        ok={{
                            onClick: () => {
                                setShowMessageModalValidDocument({ show: false, message: "" });
                            },
                        }}
                        cancel={{ onClick: () => setShowMessageModalValidDocument({ show: false, message: "" }) }}
                    />
                )}

                {showMessageModalValidBaseDocument && 
                    <MessageModalWindow
                        message={t("errors.noStoreForBaseDocument")}
                        ok={{ onClick: () => setShowMessageModalValidBaseDocument(false) }}
                        cancel={{ onClick: () => setShowMessageModalValidBaseDocument(false) }}
                    />
                }

                {showMessageModal.show && (	
                    <MessageModalWindow	
                        message={showMessageModal.message}	
                        ok={{	
                            onClick: () => {	
                                setShowMessageModal({ show: false, message: "" });	
                                showMessageModal.btSaveOrOk === "ok" ? onOkAction() : confirmSave(props.save);	
                            },	
                        }}	
                        cancel={{	
                            onClick: () => {	
                                setShowMessageModal({ show: false, message: "" });	
                            },	
                        }}	
                        primary	
                    />	
                )}

                {selectBaseDocument && (
                    <SelectDocumentModalWindow
                    idStoreGlobal={invoiceOut?.store?.value?.idGlobal ?? uuidNull}
                    dateDocument={invoiceOut.documentDate.value ?? DateTime.now()}
                        ok={(lots, document) => {
                            const values: IReturnCreatedItem[] = lots.map((x) => {
                                const idGlobal: string = uuidv4();
                                return {
                                    displayItem: {
                                        idGlobal: idGlobal,
                                        idDocumentGlobal: props.idGlobal,
                                        lotName: x.lotName,
                                        producer: x.producer.displayName,
                                        goodsCode: x.goodsCode,
                                        quantity: x.quantityRem,
                                        retailVat: x.retailCostInfo?.vat ?? 0,
                                        retailSum: x.retailCostInfo?.sum ?? 0,
                                        retailSumIncVat: x.retailCostInfo?.sumIncVat ?? 0,
                                        retailVatSum: x.retailCostInfo?.vat ?? 0,
                                        retailPrice: x.retailCostInfo?.price ?? 0,
                                        retailPriceIncVat: x.retailCostInfo?.priceIncVat ?? 0,
                                        supplierVat: x.supplierCostInfo?.vat ?? 0,
                                        supplierSum: x.supplierCostInfo?.sum ?? 0,
                                        supplierSumIncVat: x.supplierCostInfo?.sumIncVat ?? 0,
                                        supplierVatSum: x.supplierCostInfo?.vatSum ?? 0,
                                        supplierPrice: x.supplierCostInfo?.price ?? 0,
                                        supplierPriceIncVat: x.supplierCostInfo?.priceIncVat ?? 0,
                                        discountVat: 0,
                                        discountSum: 0,
                                        discountSumIncVat: 0,
                                        discountVatSum: 0,
                                        discountPrice: 0,
                                        discountPriceIncVat: 0,
                                        isKiz: x.isKiz,
                                        isKizBox: x.isKizBox,
                                        supplier: x.supplier,
                                        dateCreated: x.docDate,
                                        idLotGlobal: x.idGlobal
                                    },
                                    createdItem: {
                                        idGlobal: idGlobal,
                                        idGoodsGlobal: x.goods.idGlobal,
                                        idScalingRatioGlobal: x.scalingRatio.idGlobal,
                                        idLotGlobal: x.idGlobal,
                                        idSupplierGlobal: x.idSupplierGlobal,
                                        contractorPricePerUnit: 0,
                                        price: 0,
                                        priceVat: x.retailCostInfo?.vat ?? 0,
                                        vatSup: 0,
                                        isKiz: x.isKiz,
                                        discountCostInfo: {
                                            adprice: 0,
                                            price: 0,
                                            priceIncVat: 0,
                                            sum: 0,
                                            sumIncVat: 0,
                                            vat: 0,
                                            vatPrice: 0,
                                            vatSum: 0
                                        } as ICostInfoDTO,
                                        retailCostInfo: x.retailCostInfo,
                                        supplierCostInfo: x.supplierCostInfo,
                                        kizs: [],
                                        kizBoxes: [],
                                        quantity: x.quantityRem ?? 0,
                                    },
                                };
                            });
                            setBaseDocument(document.displayName);
                            setViewItems(values.map((x) => x.displayItem));
                            setEntity({
                                ...entity,
                                baseDocumentInfo: { idTable: document.idTable, idDocument: document.idGlobal }, 
                                items: values.map((x) => x.createdItem) as IInvoiceOutItemEditDTO[]
                            })
                            setSelectBaseDocument(false);
                        }}
                        cancel={{ onClick: () => setSelectBaseDocument(false) }}
                        onlyProc
                    />
                )}

                {сonfirmationItemsModalJsx}

            </BaseDocumentCreator>
        </>
    );
};

export default InvoiceOutCreator;
